import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

export enum OrderStatus {
    Pending = "pending",
    Paid = "paid",
    Failed = "failed",
}

export default class Order {
    id: string;
    orderReference: string;
    brandId: string;
    productType: string;
    productId: string;
    status: OrderStatus;
    createdAt: Date;
    updatedAt: Date;

    constructor(
        id: string,
        orderReference: string,
        brandId: string,
        productType: string,
        productId: string,
        status: OrderStatus = OrderStatus.Pending,
        createdAt: Date,
        updatedAt: Date
    ) {
        this.id = id;
        this.orderReference = orderReference;
        this.brandId = brandId;
        this.productType = productType;
        this.productId = productId;
        this.status = status;
        this.createdAt = createdAt;
        this.updatedAt = updatedAt;
    }

    /**
     * Create a new order.
     *
     * @param uuid User token
     * @param brandKey UUID of the brand
     * @param subscriptionKey UUID of the subscription
     * @param orderReference Unique order reference
     */
    public static readonly create = async (
        uuid: string,
        brandKey: string,
        subscriptionKey: string,
        orderReference: string
    ) =>
        (await axios
            .post(
                `${environment.api_url}/order/create`,
                {
                    brandKey: brandKey,
                    subscriptionKey: subscriptionKey,
                    orderReference: orderReference,
                },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as
            | { success: boolean; orderId: string }
            | undefined;

    /**
     * Fetch order details.
     *
     * @param uuid User token
     * @param orderId ID of the order
     */
    public static readonly info = async (uuid: string, orderId: string) =>
        (await axios
            .get(`${environment.api_url}/order/${orderId}`, {
                headers: {
                    "User-Token": uuid,
                },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as Order | undefined;

    /**
     * List all orders for a brand.
     *
     * @param uuid User token
     * @param brandId ID of the brand
     */
    public static readonly list = async (uuid: string, brandId: string) =>
        (await axios
            .get(`${environment.api_url}/brand/${brandId}/order`, {
                headers: {
                    "User-Token": uuid,
                },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as Order[] | undefined;

    /**
     * Update the status of an order.
     *
     * @param uuid User token
     * @param orderId ID of the order
     * @param status New status of the order
     */
    public static readonly updateStatus = async (
        uuid: string,
        orderId: string,
        status: OrderStatus
    ) =>
        axios.put(
            `${environment.api_url}/order/${orderId}`,
            {
                status,
            },
            {
                headers: {
                    "User-Token": uuid,
                },
            }
        );
}
